import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Download, Loader2, PenLine } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { API_URL, fmtDate } from './rateUtils';

const storageKey = (token) => `rate-signature-${token}`;

export const loadSavedSignature = (token) => {
  try { return JSON.parse(localStorage.getItem(storageKey(token))); } catch { return null; }
};

export const AcknowledgeForm = ({ token, text, signature, onSigned }) => {
  const [form, setForm] = useState({ full_name: '', email: '', initials: '', acknowledged: false });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.acknowledged) { toast.error('Please check the acknowledgment box'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/rate-cards/shared/${token}/sign`, { ...form, email: form.email || null });
      localStorage.setItem(storageKey(token), JSON.stringify(res.data));
      onSigned(res.data);
      toast.success('Acknowledgment signed. Thank you!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Please check your name, email and initials');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="relative bg-[#12121A] border border-violet-500/30 rounded-2xl p-5 sm:p-7 overflow-hidden" data-testid="rate-acknowledgment">
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-violet-600/20 blur-3xl rounded-full pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <PenLine size={18} className="text-violet-300" />
          <h3 className="text-base sm:text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Acknowledgment</h3>
        </div>
        <p className="text-sm text-slate-300 leading-relaxed mb-5">{text}</p>

        {signature ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4" data-testid="rate-signed-state">
            <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={28} />
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold">Signed by {signature.full_name} ({signature.initials})</p>
              <p className="text-xs text-slate-400">{fmtDate(signature.acknowledged_at)}</p>
            </div>
            <a
              href={`${API_URL}/rate-cards/shared/${token}/pdf?signature_id=${signature.id}`}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
              data-testid="download-signed-pdf"
            >
              <Download size={16} /> Download signed PDF
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_120px] gap-4" data-testid="rate-sign-form">
            <div>
              <Label className="text-slate-300 text-sm">Full name *</Label>
              <Input required minLength={2} value={form.full_name} onChange={(e) => set('full_name', e.target.value)} className="mt-1 bg-[#0A0A0F] border-slate-700 text-white" data-testid="sign-full-name" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Email (optional)</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-1 bg-[#0A0A0F] border-slate-700 text-white" data-testid="sign-email" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Initials *</Label>
              <Input required maxLength={6} value={form.initials} onChange={(e) => set('initials', e.target.value.toUpperCase())} className="mt-1 bg-[#0A0A0F] border-slate-700 text-white font-mono tracking-widest uppercase" data-testid="sign-initials" />
            </div>
            <label className="sm:col-span-3 flex items-start gap-3 cursor-pointer select-none">
              <Checkbox checked={form.acknowledged} onCheckedChange={(v) => set('acknowledged', v === true)} className="mt-0.5 border-slate-500 data-[state=checked]:bg-violet-600" data-testid="sign-acknowledge-checkbox" />
              <span className="text-sm text-slate-300">I acknowledge I have received and fully understand this rate card and the compensation methods outlined.</span>
            </label>
            <button
              type="submit"
              disabled={sending}
              className="sm:col-span-3 sm:justify-self-start inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 disabled:opacity-60 transition-colors"
              data-testid="sign-submit"
            >
              {sending ? <Loader2 className="animate-spin" size={16} /> : <PenLine size={16} />}
              Sign acknowledgment
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
